<script lang="ts">
  import { layout } from '../state/layout.svelte'
  import { isNativeShell } from '../native'
  import { isIos } from '../pwa.svelte'
  import Icon from './Icon.svelte'
  import SearchForm from './SearchForm.svelte'

  /**
   * The first run with location blocked (round 2's 3.7): nothing to name, so
   * nothing in the condition field. A plain title, why there is no place, a ZIP
   * field, and the map to tap. It stands where the hero would: the desktop's
   * sidebar, and the phone's Forecast section.
   *
   * Shown only when the browser has said no and there is no point at all
   * (`firstRunBlocked`) — which is a first run, because a returning visitor
   * opens to the last place they viewed.
   */

  let why = $derived(
    isNativeShell()
      ? "Fusion WX isn't allowed to see where you are."
      : `${!layout.isDesktop && isIos() ? 'Safari' : 'The browser'} isn't sharing where you are.`
  )
</script>

<section
  class="first"
  class:phone={!layout.isDesktop}
  aria-labelledby="first-title"
>
  <h1 id="first-title">Fusion WX</h1>
  <div class="note">
    <span class="mark" aria-hidden="true"><Icon name="triangle-alert" size={20} strokeWidth={2.5} /></span>
    <span class="text">
      <span class="title">Location is blocked</span>
      <span class="detail">
        {why}
        {layout.isDesktop
          ? 'Search for a town or ZIP to start. You can allow location later from the site controls.'
          : 'Search for a town or ZIP to start, or tap a point on the Map.'}
      </span>
    </span>
  </div>
  <div class="zip">
    {#if layout.isDesktop}<span class="label" aria-hidden="true">Town or ZIP</span>{/if}
    <SearchForm variant="start" />
  </div>
  {#if layout.isDesktop}
    <p class="tap">Or tap anywhere on the map.</p>
  {:else}
    <details class="how">
      <summary>How to allow location</summary>
      <p>
        {isNativeShell()
          ? 'Open the iPhone’s Settings, then Apps, then Fusion WX, then Location, and choose While Using the App. Then reopen the app.'
          : isIos()
          ? 'Open the iPhone’s Settings, then Privacy & Security, then Location Services. Turn it on, and set Safari Websites (or Fusion WX, if it is installed) to While Using. Then reopen the app.'
          : 'Tap the icon beside the address, open Permissions, allow Location, then reload.'}
      </p>
    </details>
  {/if}
</section>

<style>
  .first {
    display: flex;
    flex-direction: column;
    gap: 18px;
    padding: 28px 24px;
    background: var(--color-bg);
    color: var(--color-text);
  }

  /* The phone's Forecast section, under its panel bar. */
  .first.phone {
    gap: 16px;
    padding: 8px var(--gutter, 16px) 20px;
    --start-h: 48px;
  }

  h1 {
    margin: 0;
    font-size: 28px;
    font-weight: 800;
    line-height: 1.05;
    letter-spacing: -0.02em;
  }

  .phone h1 {
    font-size: 26px;
  }

  .note {
    display: grid;
    grid-template-columns: 20px minmax(0, 1fr);
    gap: 10px;
    padding: 12px 0;
    border-top: 2px solid var(--color-text);
    border-bottom: 1px solid var(--color-divider);
  }

  .mark {
    color: var(--color-warn);
  }

  .text {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .title {
    font-size: 14px;
    font-weight: 800;
    color: var(--color-warn);
  }

  .detail {
    font-size: 13px;
    line-height: 1.45;
  }

  .phone .title {
    font-size: 15px;
  }

  .phone .detail {
    font-size: 14px;
  }

  .zip {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .label {
    font-size: 12px;
    font-weight: 800;
  }

  .tap {
    margin: 0;
    font-size: 13px;
    color: var(--color-muted);
  }

  .how summary {
    font-size: 14px;
    font-weight: 800;
    text-decoration: underline;
    text-underline-offset: 3px;
    cursor: pointer;
    list-style: none;
  }

  .how summary::-webkit-details-marker {
    display: none;
  }

  .how p {
    margin: 8px 0 0;
    font-size: 14px;
    line-height: 1.45;
  }
</style>
